import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import type { Role } from './types/api';
import Spinner from './components/ui/Spinner';
import AppShell from './components/layout/AppShell';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import TaskListPage from './pages/tasks/TaskListPage';
import TaskDetailPage from './pages/tasks/TaskDetailPage';
import TaskCreatePage from './pages/tasks/TaskCreatePage';
import CheckInPage from './pages/CheckInPage';
import CheckInHistoryPage from './pages/CheckInHistoryPage';
import CheckInStatusPage from './pages/CheckInStatusPage';
import ProjectsPage from './pages/ProjectsPage';
import NotificationsPage from './pages/NotificationsPage';
import FeedbackSubmitPage from './pages/feedback/FeedbackSubmitPage';
import PulsePage from './pages/PulsePage';
import EngineersPage from './pages/engineers/EngineersPage';
import EngineerDetailPage from './pages/engineers/EngineerDetailPage';
import EscalationsPage from './pages/EscalationsPage';
import LeadershipReportPage from './pages/reports/LeadershipReportPage';
import FeedbackInboxPage from './pages/feedback/FeedbackInboxPage';
import UsersPage from './pages/admin/UsersPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import ThresholdsPage from './pages/admin/ThresholdsPage';
import TeamsPage from './pages/admin/TeamsPage';
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';
import ProjectDetailPage from './pages/projects/ProjectDetailPage';
import SprintsPage from './pages/sprints/SprintsPage';
import SprintDetailPage from './pages/sprints/SprintDetailPage';
import SetupPage from './pages/SetupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import NotFoundPage from './pages/NotFoundPage';
import ImportPage from './pages/ImportPage';
import EpicDetailPage from './pages/epics/EpicDetailPage';
import StandupSummaryPage from './pages/standup/StandupSummaryPage';

function PrivateRoute({ minRole }: { minRole?: Role }) {
  const { currentUser, isLoading, can } = useAuth();
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (!currentUser) return <Navigate to="/login" replace />;
  if (minRole && !can(minRole)) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/setup', element: <SetupPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  {
    element: <PrivateRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: 'dashboard', element: <DashboardPage /> },
          { path: 'tasks', element: <TaskListPage /> },
          { path: 'tasks/:id', element: <TaskDetailPage /> },
          { path: 'check-in', element: <CheckInPage /> },
          { path: 'check-in/history', element: <CheckInHistoryPage /> },
          { path: 'projects', element: <ProjectsPage /> },
          { path: 'projects/:id', element: <ProjectDetailPage /> },
          { path: 'epics/:id', element: <EpicDetailPage /> },
          { path: 'notifications', element: <NotificationsPage /> },
          { path: 'notifications/preferences', element: <NotificationPreferencesPage /> },
          { path: 'feedback/new', element: <FeedbackSubmitPage /> },
          { path: 'pulse', element: <PulsePage /> },
          {
            element: <PrivateRoute minRole="team_lead" />,
            children: [
              { path: 'sprints', element: <SprintsPage /> },
              { path: 'sprints/:id', element: <SprintDetailPage /> },
              { path: 'standup', element: <StandupSummaryPage /> },
            ],
          },
          {
            element: <PrivateRoute minRole="project_manager" />,
            children: [
              { path: 'tasks/new', element: <TaskCreatePage /> },
              { path: 'engineers', element: <EngineersPage /> },
              { path: 'engineers/:id', element: <EngineerDetailPage /> },
              { path: 'escalations', element: <EscalationsPage /> },
              { path: 'check-in/status', element: <CheckInStatusPage /> },
              { path: 'import', element: <ImportPage /> },
            ],
          },
          {
            element: <PrivateRoute minRole="head_of_rd" />,
            children: [
              { path: 'reports/leadership', element: <LeadershipReportPage /> },
              { path: 'feedback', element: <FeedbackInboxPage /> },
              { path: 'admin/users', element: <UsersPage /> },
              { path: 'admin/audit-log', element: <AuditLogPage /> },
              { path: 'admin/thresholds', element: <ThresholdsPage /> },
              { path: 'admin/teams', element: <TeamsPage /> },
            ],
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
